import type { NextLink, Operation } from '@apollo/client/core';
import { ApolloLink } from '@apollo/client/core';
import type { Logger } from '@datadog/browser-logs';

interface LoggerLinkOptions {
  enabled: boolean;
  logger: Logger | Console;
}

/**
 * Logger middleware for Apollo Client that will automatically log information
 * about every GraphQL operation. This mirrors the TRPC loggerLink functionality.
 */
export function createLoggerLink(options: LoggerLinkOptions): ApolloLink {
  const { enabled, logger } = options;

  return new ApolloLink((operation: Operation, forward: NextLink) => {
    if (!enabled) {
      return forward(operation);
    }

    const start = Date.now();
    const operationName = operation.operationName || 'Unknown';

    return forward(operation).map((result) => {
      const duration = Date.now() - start;

      if (result.errors && result.errors.length > 0) {
        // Errors are already logged by the errorLink, so we don't need to log them again here
        logger.debug(`GraphQL operation "${operationName}" completed with errors in ${duration}ms`);
      } else {
        logger.debug(`GraphQL operation "${operationName}" completed successfully in ${duration}ms`, {
          operationName,
          duration,
          variables: operation.variables,
        });
      }

      return result;
    });
  });
}
